import { Button, Heading, ModalBody, ModalFooter, ModalHeader, Stack } from '@chakra-ui/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SpeakerContactForm,
  SpeakerContactFormFieldset,
  SpeakerContactFormSubmit,
} from '@/components/ui/shared/profile/SpeakerContactForm/SpeakerContactForm';
import { useAnalytics } from '@/context/Analytics';
import { EmailSourceType } from '@/enums/ProfileContacts';
import { IToastType } from '@/enums/ToastType';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import useSession from '@/hooks/useSession';
import { useToast } from '@/hooks/useToast';
import { IUrlQueryStringParams } from '@/pages/events/[eventId]/sessions/[sessionId]/lecture/[lectureId]';
import { ThinProfile } from '@/resources/ThinProfile';

interface IContactFormModalPage {
  profile: ThinProfile;
  closeForm: () => void;
}

export const ContactFormModalPage: FC<IContactFormModalPage> = ({ profile, closeForm }) => {
  const { user } = useSession();
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const toast = useToast();

  const { eventId, lectureId } = useQueryParams<IUrlQueryStringParams>({
    eventId: StringParam,
    lectureId: StringParam,
    sessionId: StringParam,
  });

  const onSubmitSuccess = () => {
    analytics.track('event_session_speaker_email_send', {
      lecture_id: lectureId,
      speaker_name: profile.fullName,
      speaker_id: profile.id,
    });

    // backToProfile();
    toast({ status: IToastType.SUCCESS, title: t('contactForm.notifications.success') });
  };

  const onSubmitFailure = () => {
    toast({ status: IToastType.ERROR, title: t('contactForm.notifications.error') });
  };

  return (
    <SpeakerContactForm
      profile={profile}
      lectureId={lectureId}
      eventId={eventId}
      onSubmitSuccess={onSubmitSuccess}
      onSubmitFailure={onSubmitFailure}
      source={EmailSourceType.Event}
      defaultValues={{
        name: user?.fullName,
        email: user?.email,
      }}
    >
      <ModalHeader as={Heading} px={0} pb={{ base: 4, md: 8 }} fontSize={{ base: '2xl', md: '4xl' }}>
        {t('contactForm.titleDirect', { profileName: profile.fullName })}
      </ModalHeader>
      <ModalBody p={0}>
        <SpeakerContactFormFieldset />
      </ModalBody>
      <ModalFooter px={0}>
        <Stack
          w="full"
          justifyContent="space-between"
          flexWrap="wrap"
          spacing={8}
          direction={{ base: 'column', sm: 'row' }}
        >
          <Button variant="outline" size="sm" onClick={closeForm}>
            {t('contactForm.actions.returnToProfile')}
          </Button>
          <SpeakerContactFormSubmit size="sm">{t('contactForm.actions.send')}</SpeakerContactFormSubmit>
        </Stack>
      </ModalFooter>
    </SpeakerContactForm>
  );
};
